import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, ListPlus, Plus, Trash } from 'phosphor-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { IDataNoPagination } from '../../types/shared';
import { IUserField, IUserFieldValue } from '../../hooks/useUserFields';
import { useDialog } from '../../providers/dialog';
import {
  AlternarDois,
  AZUL,
  AZUL_ESCURO,
  Cartao,
  Contador,
  Etiqueta,
  Eyebrow,
  MONO,
  TEXTO,
  TEXTO_SUAVE,
} from './ui';

/** O backend grava o nome em minúsculas com "_": aqui volta a ser legível. */
export const rotuloDoCampo = (campo: string) => {
  const t = campo.replace(/_/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
};
const nomeGravado = (texto: string) =>
  texto
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const TIPO: Record<string, string> = {
  text: 'Texto',
  number: 'Número',
  // a tela antiga criava "Seleção", mas nunca teve onde cadastrar as opções
  select: 'Texto',
};

const SUGESTOES = ['Matrícula', 'Ramal', 'Cargo', 'Centro de custo', 'Unidade'];

const EXEMPLO = [
  { campo: 'Matrícula', valor: '0142' },
  { campo: 'Ramal', valor: '2031' },
  { campo: 'Centro de custo', valor: 'Fiscal SP' },
];

const erroDaApi = (e: any, padrao: string) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return padrao;
};

export const camposApi = {
  campos: async () =>
    (await api.get<IDataNoPagination<IUserField[]>>('/api/v1/usuarios/campos/')).data.data ?? [],
  criar: async (campo: string, tipo: string) =>
    api.post('/api/v1/usuarios/campos/', { campo, tipo }),
  remover: async (campo: string) => api.delete(`/api/v1/usuarios/campos/${campo}/`),
  valores: async (usuarioId: number) =>
    (await api.get<IDataNoPagination<IUserFieldValue[]>>(`/api/v1/usuarios/${usuarioId}/valores/`))
      .data.data ?? [],
  gravar: async (usuarioId: number, campo: string, value: string) =>
    api.post(`/api/v1/usuarios/${usuarioId}/valores/`, { campo, value }),
  apagar: async (usuarioId: number, campo: string) =>
    api.delete(`/api/v1/usuarios/${usuarioId}/valores/${campo}/`),
};

/** Cartãozinho de pessoa com campos extras: o mesmo desenho no exemplo e na prévia. */
function MiniCadastro({
  nome,
  campos,
  exemplo,
}: {
  nome: string;
  campos: { campo: string; valor: string }[];
  exemplo?: boolean;
}) {
  return (
    <Box
      p="12px 14px"
      borderRadius="14px"
      bg="white"
      boxShadow="inset 0 0 0 1px rgba(15,23,42,.08), 0 6px 18px -10px rgba(15,23,42,.25)"
      minW="220px"
    >
      <Flex align="center" gap="8px" mb="8px">
        <Flex
          w="28px"
          h="28px"
          borderRadius="50%"
          align="center"
          justify="center"
          fontSize="11px"
          fontWeight={700}
          bg="#DFF3FF"
          color="#075985"
        >
          {nome
            .split(' ')
            .slice(0, 2)
            .map(p => p[0])
            .join('')}
        </Flex>
        <Text fontSize="13px" fontWeight={700} color={TEXTO} flex={1}>
          {nome}
        </Text>
        {exemplo && <Etiqueta tom="neutro">exemplo</Etiqueta>}
      </Flex>
      <Flex direction="column" gap="4px">
        {campos.map(c => (
          <Flex key={c.campo} justify="space-between" gap="12px" fontSize="12.5px">
            <Text color={TEXTO_SUAVE}>{c.campo}</Text>
            <Text fontWeight={600} color={TEXTO} fontFamily={MONO}>
              {c.valor || '—'}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
}

/** "Campos do cadastro": quais informações a mais todo colaborador pode ter. */
export function CamposDoCadastroModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const showDialog = useDialog();
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState<'text' | 'number'>('text');

  const { data: campos = [], isLoading } = useQuery({
    queryKey: ['userFields'],
    queryFn: camposApi.campos,
    enabled: isOpen,
  });
  const gravado = nomeGravado(nome);
  const repetido = campos.some(c => c.campo === gravado);

  const criar = useMutation({
    mutationFn: () => camposApi.criar(gravado, tipo),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['userFields'] });
      toast.success(`Campo ${rotuloDoCampo(gravado)} criado`);
      setNome('');
      setTipo('text');
    },
    onError: e => toast.error(erroDaApi(e, 'Não foi possível criar o campo')),
  });
  const remover = useMutation({
    mutationFn: (campo: string) => camposApi.remover(campo),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['userFields'] });
      queryClient.invalidateQueries({ queryKey: ['central', 'campos-do-usuario'] });
      toast.success('Campo removido');
    },
    onError: e => toast.error(erroDaApi(e, 'Não foi possível remover o campo')),
  });

  const previa = [
    ...campos.map(c => ({ campo: rotuloDoCampo(c.campo), valor: '' })),
    ...(gravado && !repetido ? [{ campo: rotuloDoCampo(gravado), valor: '' }] : []),
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="rgba(15,23,42,.35)" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="22px" bg="#F8FAFC" overflow="hidden" mx="16px">
        <ModalCloseButton borderRadius="999px" />
        <ModalBody
          p={{ base: '18px', md: '24px' }}
          display="flex"
          flexDirection="column"
          gap="16px"
        >
          <Flex gap="12px" align="center">
            <Flex
              w="42px"
              h="42px"
              borderRadius="14px"
              align="center"
              justify="center"
              bg="#DFF3FF"
              color={AZUL}
              flex="none"
            >
              <ListPlus size={22} weight="duotone" />
            </Flex>
            <Box>
              <Eyebrow>USUÁRIOS</Eyebrow>
              <Text fontSize="20px" fontWeight={700} color={TEXTO} lineHeight="26px">
                Campos do cadastro
              </Text>
            </Box>
          </Flex>

          <Cartao p="16px 18px" display="flex" flexDirection="column" gap="12px">
            <Eyebrow>COMO FUNCIONA</Eyebrow>
            <Flex gap="14px" align="center" wrap="wrap">
              <Box flex="1 1 200px">
                <Text fontSize="13.5px" fontWeight={650} color={TEXTO}>
                  1. Crie o campo aqui, uma vez
                </Text>
                <Text fontSize="12.5px" color={TEXTO_SUAVE} mt="2px">
                  Ele passa a existir no cadastro de todos os colaboradores.
                </Text>
                <Flex gap="6px" wrap="wrap" mt="8px">
                  {EXEMPLO.map(e => (
                    <Etiqueta key={e.campo} tom="info">
                      {e.campo}
                    </Etiqueta>
                  ))}
                </Flex>
              </Box>
              <Box color={TEXTO_SUAVE} display={{ base: 'none', md: 'block' }}>
                <ArrowRight size={18} />
              </Box>
              <Box flex="1 1 220px">
                <Text fontSize="13.5px" fontWeight={650} color={TEXTO}>
                  2. Preencha no painel de cada pessoa
                </Text>
                <Text fontSize="12.5px" color={TEXTO_SUAVE} mt="2px" mb="8px">
                  Em Usuários, ao abrir alguém, no bloco “Campos extras”.
                </Text>
                <MiniCadastro nome="Ana Souza" campos={EXEMPLO} exemplo />
              </Box>
            </Flex>
            <Text fontSize="12px" color={TEXTO_SUAVE}>
              Campo extra é só informação: não libera telas, empresas nem ações.
            </Text>
          </Cartao>

          <Cartao p="16px 18px" display="flex" flexDirection="column" gap="10px">
            <Flex justify="space-between" align="center">
              <Eyebrow>CAMPOS CRIADOS</Eyebrow>
              <Contador>{campos.length}</Contador>
            </Flex>
            {isLoading && <Spinner size="sm" color={AZUL} />}
            {!isLoading && campos.length === 0 && (
              <Text fontSize="13px" color={TEXTO_SUAVE}>
                Nenhum campo ainda. Crie o primeiro abaixo.
              </Text>
            )}
            {campos.map(c => (
              <Flex
                key={c.id}
                align="center"
                gap="10px"
                p="8px 10px"
                borderRadius="12px"
                bg="rgba(248,250,252,.85)"
              >
                <Text fontSize="13.5px" fontWeight={600} color={TEXTO} flex={1}>
                  {rotuloDoCampo(c.campo)}
                </Text>
                <Etiqueta tom="neutro">{TIPO[c.tipo] ?? c.tipo}</Etiqueta>
                <Button
                  size="xs"
                  variant="ghost"
                  borderRadius="999px"
                  color="#991B1B"
                  aria-label={`Remover o campo ${rotuloDoCampo(c.campo)}`}
                  isLoading={remover.isPending && remover.variables === c.campo}
                  onClick={() =>
                    showDialog({
                      status: 'warning',
                      title: `Remover o campo ${rotuloDoCampo(c.campo)}?`,
                      description:
                        'O valor dele some do cadastro de todos os colaboradores. Não dá para desfazer.',
                      cancelLabel: 'Cancelar',
                      async onConfirm() {
                        await remover.mutateAsync(c.campo);
                      },
                    })
                  }
                >
                  <Trash size={14} />
                </Button>
              </Flex>
            ))}
          </Cartao>

          <Cartao p="16px 18px" display="flex" flexDirection="column" gap="12px">
            <Eyebrow>NOVO CAMPO</Eyebrow>
            <Flex gap="12px" wrap="wrap" align="flex-end">
              <Box flex="1 1 240px">
                <Input
                  h="40px"
                  borderRadius="10px"
                  bg="white"
                  borderColor="#CBD5E1"
                  placeholder="Ex.: Matrícula"
                  aria-label="Nome do novo campo"
                  value={nome}
                  onChange={e => setNome(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && gravado && !repetido) criar.mutate();
                  }}
                />
              </Box>
              <Box w={{ base: '100%', sm: '220px' }}>
                <AlternarDois
                  valor={tipo}
                  onChange={v => setTipo(v as 'text' | 'number')}
                  opcoes={[
                    { valor: 'text', rotulo: 'Texto' },
                    { valor: 'number', rotulo: 'Número' },
                  ]}
                />
              </Box>
              <Button
                h="40px"
                borderRadius="999px"
                color="white"
                bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
                _hover={{ bg: AZUL_ESCURO }}
                leftIcon={<Plus size={14} />}
                isDisabled={!gravado || repetido}
                isLoading={criar.isPending}
                onClick={() => criar.mutate()}
              >
                Criar campo
              </Button>
            </Flex>
            {repetido && (
              <Text fontSize="12px" color="#92400E">
                Já existe um campo {rotuloDoCampo(gravado)}.
              </Text>
            )}
            <Flex gap="6px" wrap="wrap" align="center">
              <Text fontSize="12px" color={TEXTO_SUAVE}>
                Sugestões:
              </Text>
              {SUGESTOES.filter(s => !campos.some(c => c.campo === nomeGravado(s))).map(s => (
                <Box
                  key={s}
                  as="button"
                  type="button"
                  fontSize="12px"
                  fontWeight={600}
                  px="10px"
                  h="26px"
                  borderRadius="999px"
                  bg="white"
                  color="#334155"
                  boxShadow="inset 0 0 0 1px rgba(15,23,42,.1)"
                  _hover={{ color: AZUL }}
                  onClick={() => setNome(s)}
                >
                  {s}
                </Box>
              ))}
            </Flex>
            {previa.length > 0 && (
              <Box>
                <Text fontSize="12px" color={TEXTO_SUAVE} mb="6px">
                  Assim fica o cadastro de cada colaborador:
                </Text>
                <Box maxW="320px">
                  <MiniCadastro nome="Colaborador" campos={previa} />
                </Box>
              </Box>
            )}
          </Cartao>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

const nomeDoValor = (v: IUserFieldValue) =>
  typeof v.campo === 'string' ? (v.campo as unknown as string) : (v.campo?.campo ?? '');

/** Bloco "Campos extras" no painel do usuário: preencher os campos do cadastro. */
export function CamposExtrasDoUsuario({ usuarioId, nome }: { usuarioId: number; nome: string }) {
  const queryClient = useQueryClient();
  const [gerenciar, setGerenciar] = useState(false);
  const { data: campos = [], isLoading: l1 } = useQuery({
    queryKey: ['userFields'],
    queryFn: camposApi.campos,
  });
  const { data: valores = [], isLoading: l2 } = useQuery({
    queryKey: ['central', 'campos-do-usuario', usuarioId],
    queryFn: () => camposApi.valores(usuarioId),
  });
  const base = useMemo(() => {
    const m: Record<string, string> = {};
    valores.forEach(v => {
      m[nomeDoValor(v)] = v.value ?? '';
    });
    return m;
  }, [valores]);
  const [rascunho, setRascunho] = useState<Record<string, string>>({});
  useEffect(() => setRascunho(base), [base]);

  const mudados = campos.filter(c => (rascunho[c.campo] ?? '') !== (base[c.campo] ?? ''));
  const salvar = useMutation({
    mutationFn: async () => {
      // um campo apagado sai do cadastro; o resto grava (a rota cria ou atualiza)
      await Promise.all(
        mudados.map(c => {
          const v = (rascunho[c.campo] ?? '').trim();
          return v ? camposApi.gravar(usuarioId, c.campo, v) : camposApi.apagar(usuarioId, c.campo);
        }),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['central', 'campos-do-usuario', usuarioId],
      });
      queryClient.invalidateQueries({ queryKey: ['userFieldValues'] });
      toast.success(`Campos extras de ${nome} salvos`);
    },
    onError: e => toast.error(erroDaApi(e, 'Não foi possível salvar os campos extras')),
  });

  const preenchidos = campos.filter(c => (base[c.campo] ?? '') !== '').length;

  return (
    <Flex direction="column" gap="12px">
      <Flex align="center" gap="8px" wrap="wrap">
        <ListPlus size={16} color={TEXTO} />
        <Text fontSize="16px" fontWeight={650} color={TEXTO}>
          Campos extras
        </Text>
        {campos.length > 0 && (
          <Contador>
            {preenchidos}/{campos.length} preenchidos
          </Contador>
        )}
        <Button
          ml="auto"
          size="sm"
          variant="ghost"
          borderRadius="999px"
          onClick={() => setGerenciar(true)}
        >
          Gerenciar campos
        </Button>
      </Flex>

      {(l1 || l2) && <Spinner size="sm" color={AZUL} />}

      {!l1 && campos.length === 0 && (
        <Flex gap="16px" align="center" wrap="wrap">
          <Box flex="1 1 260px">
            <Text fontSize="13px" color={TEXTO_SUAVE}>
              Informações a mais no cadastro de cada colaborador, como matrícula, ramal ou centro de
              custo. Ainda não há nenhum campo criado: veja ao lado como fica.
            </Text>
            <Button
              mt="10px"
              size="sm"
              borderRadius="999px"
              color="white"
              bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
              _hover={{ bg: AZUL_ESCURO }}
              leftIcon={<Plus size={14} />}
              onClick={() => setGerenciar(true)}
            >
              Criar o primeiro campo
            </Button>
          </Box>
          <Box opacity={0.85}>
            <MiniCadastro nome="Ana Souza" campos={EXEMPLO} exemplo />
          </Box>
        </Flex>
      )}

      {!l1 && !l2 && campos.length > 0 && (
        <>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
            {campos.map(c => {
              const mudou = (rascunho[c.campo] ?? '') !== (base[c.campo] ?? '');
              return (
                <Box key={c.id}>
                  <Flex align="center" gap="6px" mb="4px">
                    <Eyebrow>{rotuloDoCampo(c.campo).toUpperCase()}</Eyebrow>
                    {mudou && <Etiqueta tom="aviso">alterado</Etiqueta>}
                  </Flex>
                  <Input
                    h="38px"
                    borderRadius="10px"
                    bg="white"
                    borderColor={mudou ? '#F59E0B' : '#CBD5E1'}
                    type={c.tipo === 'number' ? 'number' : 'text'}
                    aria-label={rotuloDoCampo(c.campo)}
                    placeholder="Não preenchido"
                    value={rascunho[c.campo] ?? ''}
                    onChange={e => setRascunho(r => ({ ...r, [c.campo]: e.target.value }))}
                  />
                </Box>
              );
            })}
          </SimpleGrid>
          <Flex justify="flex-end" gap="8px">
            <Button
              size="sm"
              variant="ghost"
              borderRadius="999px"
              isDisabled={!mudados.length}
              onClick={() => setRascunho(base)}
            >
              Desfazer
            </Button>
            <Button
              size="sm"
              borderRadius="999px"
              color="white"
              bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
              _hover={{ bg: AZUL_ESCURO }}
              isDisabled={!mudados.length}
              isLoading={salvar.isPending}
              onClick={() => salvar.mutate()}
            >
              Salvar campos extras
            </Button>
          </Flex>
        </>
      )}

      <CamposDoCadastroModal isOpen={gerenciar} onClose={() => setGerenciar(false)} />
    </Flex>
  );
}
